import React, { createContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import { renderRoutes } from 'react-router-config';
import { ClickAwayListener, Drawer, Hidden } from '@material-ui/core';
import clsx from 'clsx';
import { useHistory, useLocation } from 'react-router';
import ModalSupport from 'components/Support/Modal';
import ModalLogin from 'components/Login/Modal';
import Box from 'core/ui/Box';
import SearchWorkoutFilters from 'core/ui/SearchChosen/Filters';
import { searchActions } from 'modules/search';
import { useDispatch, useSelector } from 'react-redux';
import ModalAddPr from 'components/Profile/Dashboard/PersonalRecord/ModalAddPr';
import ConfirmDialog from 'core/ui/ConfirmDialog';
import { useJsApiLoader } from '@react-google-maps/api';
import { GM_API_KEY } from 'core/env';
import { sessionSelectors } from 'modules/session';
import ModalChannelProfileSelector from 'components/Channel/ModalChannelProfileSelector';
import ModalChat from 'components/GroupsChat/ModalChat';
import { HelpVideoManagerProvider } from 'contexts/HelpVideoManagerContext';
import AppBar from './components/AppBar';
import AppBarMobile from './components/AppBarMobile';
import SideBar from './components/SideBar';
import { HelpCollapse } from './components/Helper/HelpCollapse';

export const LayoutContext = createContext();
const drawerWidth = 250;
const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  },
  toolbar: {
    height: '72px',
    [theme.breakpoints.down('sm')]: {
      height: '45px',
    },
  },
  drawer: {
    justifyContent: 'center',
    flexShrink: 0,
    border: 'none',
    backgroundColor: '#404041',
  },
  drawerOpen: {
    width: drawerWidth,
    border: 'none',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerClose: {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: 0,
    border: 'none',
  },
  content: {
    flexGrow: 1,
    flex: 1,
    [theme.breakpoints.down('sm')]: {
      paddingBottom: '66px',
    },
  },
}));

const libraries = ['geometry', 'places'];

function Layout({ route: { routes } }) {
  const isAuthenticated = useSelector((state) => sessionSelectors.getIsAuthenticated(state));
  const currentUser = useSelector((state) => sessionSelectors.getUser(state));
  const classes = useStyles();
  const location = useLocation();
  const [titleBar, setTitleBar] = useState('');
  const [modalPrScore, setModalPrScore] = useState('');
  const [refreshScreen, setRefreshScreen] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [drawerFilters, setDrawerFilters] = useState({
    open: false,
    execSearch: false,
  });
  const [activeMenu, setActiveMenu] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false });
  const [modalSupport, setModalSupport] = useState({ open: false });
  const [modalLogin, setModalLogin] = useState({ open: false });
  const [modalChat, setModalChat] = useState({ open: false });
  const [heightHeaderWrapper, setHeightHeaderWrapper] = useState(0);
  const history = useHistory();
  const dispatch = useDispatch();

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GM_API_KEY,
    libraries,
    language: 'en',
  });

  useEffect(() => {
    setDrawer(false);
    setDrawerFilters((prev) => ({ ...prev, open: false }));
    dispatch(searchActions.clearSearchResults());
  }, [history.location, dispatch]);

  return (
    <div className={classes.root}>
      <CssBaseline />
      <LayoutContext.Provider
        value={{
          activeMenu,
          setActiveMenu,
          drawer,
          setDrawer,
          titleBar,
          drawerWidth,
          setTitleBar,
          modalSupport,
          setModalSupport,
          setModalLogin,
          modalLogin,
          drawerFilters,
          setDrawerFilters,
          setHeightHeaderWrapper,
          heightBanner: heightHeaderWrapper,
          heightHeader: 72,
          refreshScreen,
          setRefreshScreen,
          confirmDialog,
          setConfirmDialog,
          addPrScore: () => setModalPrScore({ open: true }),
          isMapsLoaded: isLoaded,
          modalChat,
          setModalChat,
        }}
      >
        <HelpVideoManagerProvider>
          <ConfirmDialog
            open={confirmDialog.open}
            confirm={() => {
              if (confirmDialog.confirm) {
                confirmDialog.confirm();
              }
              setConfirmDialog({ open: false });
            }}
            cancel={() =>
              confirmDialog?.cancel
                ? confirmDialog?.cancel(() => setConfirmDialog({ open: false }))
                : setConfirmDialog({ open: false })
            }
            title={confirmDialog?.title}
            description={confirmDialog?.description}
            labelConfirm={confirmDialog?.labelConfirm}
            labelCancel={confirmDialog?.labelCancel}
            hideNoButton={confirmDialog?.hideNoButton}
          />
          <ClickAwayListener onClickAway={() => setDrawer(false)}>
            <div>
              <AppBar />
              <div className={classes.toolbar} />

              <Drawer
                variant="permanent"
                className={clsx(classes.drawer, {
                  [classes.drawerOpen]: drawerFilters.open,
                  [classes.drawerClose]: !drawerFilters.open,
                })}
                classes={{
                  paper: clsx({
                    [classes.drawerOpen]: drawerFilters.open,
                    [classes.drawerClose]: !drawerFilters.open,
                  }),
                }}
                anchor="left"
              >
                <SearchWorkoutFilters />
              </Drawer>

              <Drawer
                variant="permanent"
                className={clsx(classes.drawer, {
                  [classes.drawerOpen]: drawer,
                  [classes.drawerClose]: !drawer,
                })}
                classes={{
                  paper: clsx({
                    [classes.drawerOpen]: drawer,
                    [classes.drawerClose]: !drawer,
                  }),
                }}
                anchor="right"
              >
                {drawer && <SideBar />}
              </Drawer>
            </div>
          </ClickAwayListener>

          <Hidden smDown>
            <Box style={{ zIndex: 200, top: 72, position: 'sticky' }}>
              <HelpCollapse />
            </Box>
          </Hidden>

          <>{renderRoutes(routes)}</>

          <Hidden only={['md', 'lg', 'xl']}>
            <Box style={{ zIndex: 200, bottom: 72, position: 'sticky' }}>
              <HelpCollapse />
            </Box>

            <AppBarMobile />
          </Hidden>

          {location?.pathname?.indexOf('/channel') === -1 &&
            location?.pathname?.indexOf('/calendar') === -1 &&
            location?.pathname?.indexOf('/competition') === -1 &&
            isAuthenticated &&
            !!currentUser?.name &&
            !currentUser?.channel?.id && <ModalChannelProfileSelector noRedirect />}

          {modalSupport.open && <ModalSupport />}
          {modalLogin.open && <ModalLogin />}
          {modalPrScore.open && <ModalAddPr open close={() => setModalPrScore({ open: false })} />}
          {modalChat.open && (
            <ModalChat
              modalChat={modalChat}
              open={modalChat.open}
              close={() => setModalChat({ open: false })}
            />
          )}
        </HelpVideoManagerProvider>
      </LayoutContext.Provider>
    </div>
  );
}

Layout.propTypes = {
  route: PropTypes.shape({
    routes: PropTypes.array,
  }).isRequired,
};

export default Layout;
